import { Form } from "antd";
import { Autocomplete } from "components/UI";
import useSuggestion from "hooks/useSuggestion";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cleanBlankValue } from "utils/helper";
import { useInfiniteProvinceSuggestion } from "queries/location";
import { isEmpty } from "lodash-es";

const ProvinceSelector = ({
  autocompleteProps,
  name = "province_id",
  defaultValue = {},
  countrySelected = {
    id: 1,
    name: "Indonesia",
    currency_code: "IDR",
    country_code_alpha3: "IDN",
    country_code_alpha2: "ID",
  },
  ...props
}) => {
  const {
    debouncedSearch,
    onDropdownVisible,
    getPopupContainer,
    onPopUpScroll,
    containerId,
    onSearch,
    popUpClassName,
    open,
  } = useSuggestion({
    name,
    module: "province",
  });
  const { t } = useTranslation();

  const params = useMemo(() => {
    const newParams = {
      search: debouncedSearch,
      country_id: countrySelected?.country_id,
    };

    return cleanBlankValue(newParams);
  }, [debouncedSearch, countrySelected?.country_id]);

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteProvinceSuggestion(params, {
      refetchOnMount: false, // karena datanya tidak pernah berubah, jadi ketika remount tidak perlu refetch
    });

  const formattedData = useMemo(() => {
    let newData = data?.pages.flatMap((page) => page.data) || [];
    if (
      !newData.some((data) => data.id === defaultValue?.id) &&
      !isEmpty(defaultValue)
    ) {
      newData.unshift(defaultValue);
    }
    return newData.map((row) => {
      return {
        ...row,
        value: row?.id,
        label: row?.name,
      };
    });
  }, [data?.pages, defaultValue]);

  const popUpScrollHandler = useCallback(
    (e) => {
      onPopUpScroll(e, { hasNextPage, isFetchingNextPage, fetchNextPage });
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage, onPopUpScroll]
  );

  return (
    <Form.Item id={containerId} name={name} {...props}>
      <Autocomplete
        placeholder={t("province")}
        options={formattedData}
        allowClear
        onSearch={onSearch}
        infiniteScroll
        onPopupScroll={popUpScrollHandler}
        onDropdownVisibleChange={onDropdownVisible}
        loading={isLoading}
        loadingNextPage={isLoading || isFetchingNextPage}
        getPopupContainer={getPopupContainer}
        popupClassName={popUpClassName}
        open={open}
        optionLabelProp="label"
        {...autocompleteProps}
      />
    </Form.Item>
  );
};

export default ProvinceSelector;
